import { useContext, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Tab,
  TabValue,
  makeStyles,
} from '@fluentui/react-components';
import { CheckmarkFilled, EditOffRegular, EditRegular, OpenRegular, PeopleRegular } from '@fluentui/react-icons';

import { useNavigate } from 'react-router-dom';

import DocumentContext from '../../contexts/DocumentContext.context';
import { AI_EDIT_REVIEW_LOCK_REASON } from '../DocumentControlsLock';
import { isAiEditReviewActive } from '../../tiptap/plugins/AiEditReviewPlugin';
import DocumentRibbon from './DocumentRibbon';
import DocumentSaveStatus from './DocumentSaveStatus';
import getAiDisabledDocumentReason, { AI_DISABLED_MESSAGE } from '../../helpers/getAiDisabledDocumentReason.helper';

import { ControlledTooltip, DateLabel, TOverflowButtonParam } from '@shared/components';
import { AccessType, useCanEdit } from '@modules/access';
import CommentsHeaderButton from '@modules/comments/components/CommentsHeaderButton';
import { MockAiFindingsButton } from '@modules/mocks';
import { AiAssistantSidebarButton, useCanUseAiAssistantSidebar } from '@modules/ai';
import { GENERIC_ERROR_MESSAGE, TERMINOLOGY } from '@shared/i18n';
import useEditorActive from '../../hooks/useEditorActive.hook';

const useStyles = makeStyles({
  menuButton: {
    '& .fui-MenuButton__menuIcon': {
      fontSize: '15px',
      marginBottom: '6px',
    },
  },
});

type TProps = {
  selectedTab: TabValue;
  setSelectedTab: (value: TabValue) => void;
  handleShowAccess: () => void;
};
const Header = ({ selectedTab, setSelectedTab, handleShowAccess }: TProps) => {
  const {
    doc,
    replica,
    version,
    permissions,
    isEdit,
    hasDraft,
    requestEdit,
    editDraft,
    viewMode,
    completeVersion,
    hasUnsupportedContent,
  } = useContext(DocumentContext);
  const { canEdit } = useCanEdit(permissions.accessRight);
  const canUseAiAssistantSidebar = useCanUseAiAssistantSidebar();
  const aiDisabledReason = getAiDisabledDocumentReason(doc);
  const aiDisabledMessage = aiDisabledReason ? AI_DISABLED_MESSAGE[aiDisabledReason] : undefined;
  const navigate = useNavigate();

  useEffect(() => {
    if (isEdit && ['history', 'approvals'].includes(selectedTab as string)) setSelectedTab('home');
  }, [isEdit]);

  const connecting = requestEdit && !isEdit;
  const isAiEditLocked = useEditorActive((editor) => isAiEditReviewActive(editor.state), false);
  const aiEditLockMessage = isAiEditLocked ? AI_EDIT_REVIEW_LOCK_REASON : undefined;
  const menuItems: (TOverflowButtonParam & { show: boolean })[] = [
    {
      children: hasDraft ? 'Edit draft' : 'Create draft',
      icon: connecting ? <Spinner size="tiny" /> : <EditRegular />,
      onClick: editDraft,
      show: !isEdit && canEdit && !replica,
      disabled: connecting || hasUnsupportedContent,
      disabledMessage: hasUnsupportedContent ? GENERIC_ERROR_MESSAGE : undefined,
    },
    { children: 'Stop editing', icon: <EditOffRegular />, onClick: viewMode, show: isEdit },
    {
      children: `View ${TERMINOLOGY.master.lower}`,
      icon: <OpenRegular />,
      onClick: () => navigate(`/document/${doc._id}`),
      show: Boolean(replica),
    },
    {
      children: 'Complete version',
      icon: <CheckmarkFilled />,
      onClick: completeVersion,
      show: !replica && Boolean(doc.draft),
      disabled: hasUnsupportedContent,
      disabledMessage: hasUnsupportedContent ? GENERIC_ERROR_MESSAGE : undefined,
    },
  ];

  const classes = useStyles();
  const accessButton = (appearance: 'primary' | 'secondary') =>
    permissions.accessRight >= AccessType.MANAGE ? (
      <ControlledTooltip
        relationship="inaccessible"
        enable={isAiEditLocked}
        content={AI_EDIT_REVIEW_LOCK_REASON}
        withArrow={true}
      >
        <Button
          appearance={appearance}
          size="small"
          icon={<PeopleRegular />}
          onClick={handleShowAccess}
          disabled={isAiEditLocked}
        >
          Access
        </Button>
      </ControlledTooltip>
    ) : null;

  return (
    <DocumentRibbon
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
      pageTabs={
        <>
          <ControlledTooltip
            relationship="inaccessible"
            enable={isAiEditLocked}
            content={AI_EDIT_REVIEW_LOCK_REASON}
            withArrow={true}
          >
            <Tab value="history" disabled={isAiEditLocked}>
              History
            </Tab>
          </ControlledTooltip>
          <ControlledTooltip
            relationship="inaccessible"
            enable={isAiEditLocked}
            content={AI_EDIT_REVIEW_LOCK_REASON}
            withArrow={true}
          >
            <Tab value="approvals" disabled={isAiEditLocked}>
              Approvals
            </Tab>
          </ControlledTooltip>
        </>
      }
      actions={
        <>
          <DocumentSaveStatus />
          <MockAiFindingsButton documentName={doc.name} />
          {canUseAiAssistantSidebar ? (
            <>
              {accessButton('secondary')}
              <CommentsHeaderButton item={doc} version={version} disabledMessage={aiEditLockMessage} />
              <AiAssistantSidebarButton disabledTooltip={aiDisabledMessage} />
            </>
          ) : (
            <>
              <CommentsHeaderButton item={doc} version={version} disabledMessage={aiEditLockMessage} />

              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  {isEdit ? (
                    <MenuButton size="small" icon={<EditRegular />} className={classes.menuButton}>
                      Editing
                    </MenuButton>
                  ) : (
                    <MenuButton size="small" icon={<EditOffRegular />} className={classes.menuButton}>
                      Viewing
                    </MenuButton>
                  )}
                </MenuTrigger>
                <MenuPopover>
                  {canEdit || replica ? (
                    <MenuList>
                      {menuItems.map(({ disabled, disabledMessage, ...menuItem }, idx) =>
                        menuItem.show ? (
                          <ControlledTooltip
                            key={idx}
                            relationship="inaccessible"
                            enable={disabled}
                            content={disabledMessage || ''}
                            withArrow={true}
                            positioning="before"
                          >
                            <MenuItem {...menuItem} disabled={disabled} />
                          </ControlledTooltip>
                        ) : null
                      )}
                    </MenuList>
                  ) : (
                    <p className="p-1">No edit permission</p>
                  )}
                </MenuPopover>
              </Menu>
              {accessButton('primary')}
            </>
          )}
        </>
      }
    />
  );
};

export default Header;
